import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./styles.module.scss";

const STORAGE_KEY = "Brcom:DceAnnouncementDismissed";
const ANNOUNCEMENT_ID = "dce-2026-04";

const AnnouncementModal = () => {
  const [visible, setVisible] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const dismissed = window.localStorage.getItem(STORAGE_KEY);
    if (dismissed !== ANNOUNCEMENT_ID) {
      setVisible(true);
    }
  }, []);

  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (isAtBottom) {
      setReachedEnd(true);
    }
  }, []);

  const handleClose = () => {
    if (dontShowAgain) {
      window.localStorage.setItem(STORAGE_KEY, ANNOUNCEMENT_ID);
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Aviso Importante</h2>
        </div>
        <div
          className={styles.content}
          ref={contentRef}
          onScroll={handleScroll}
        >
          <h1 style={{ color: "#1a7f37", borderBottom: "3px solid #1a7f37", paddingBottom: 10, fontSize: "1.5em" }}>
            Obrigatoriedade da DC-e
          </h1>
          <h3>Declaração de Conteúdo Eletrônica</h3>
          <p style={{ color: "#666", marginBottom: 16 }}>
            <strong>Vigência:</strong> A partir de 06 de abril de 2026<br />
            <strong>Base legal:</strong> Ajuste SINIEF n. 05/2021 - CONFAZ
          </p>

          <hr />

          <h2>O que está mudando?</h2>
          <p>Por determinação do CONFAZ, a partir de <strong>06 de abril de 2026</strong>, toda encomenda postada nos Correios deve estar acompanhada de um documento fiscal eletrônico:</p>

          <table>
            <thead>
              <tr><th>Situação</th><th>Documento necessário</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>A chave da NF-e já está disponível no momento da pré-postagem</td>
                <td><strong>NF-e / DANFE</strong> - como já funciona hoje</td>
              </tr>
              <tr>
                <td>A chave da NF-e ainda não está disponível ou não se aplica</td>
                <td><strong>DC-e / DACE</strong> - novo documento eletrônico</td>
              </tr>
            </tbody>
          </table>

          <div style={{ background: "#ffebe9", borderLeft: "4px solid #cf222e", borderRadius: 4, padding: "14px 16px", margin: "16px 0" }}>
            <strong>Importante:</strong> A Declaração de Conteúdo em papel (formulário preenchido à mão ou impresso) <strong>deixa de ser aceita</strong> a partir de 06/04/2026.
          </div>

          <hr />

          <h2>Como funciona na BRCom?</h2>

          <h3>Quando a chave da NF-e já está disponível</h3>
          <div style={{ background: "#dafbe1", borderLeft: "4px solid #1a7f37", borderRadius: 4, padding: "14px 16px", margin: "16px 0" }}>
            <strong>Nenhuma ação necessária!</strong> Se a sua plataforma (VTEX, Bagy, Intelipost) já fornece a chave da NF-e, a BRCom envia automaticamente essa chave para os Correios.
          </div>

          <h3>Quando a chave da NF-e ainda não está disponível</h3>
          <div style={{ background: "#fff8c5", borderLeft: "4px solid #d4a72c", borderRadius: 4, padding: "14px 16px", margin: "16px 0" }}>
            <strong>Atenção:</strong> Você precisará imprimir a DACE e fixar no pacote. A BRCom cuida de tudo automaticamente.
          </div>

          <p>Isso acontece quando:</p>
          <ul>
            <li>A NF-e é emitida <strong>depois</strong> da criação do envio (ex: Bling, Tiny, Tray)</li>
            <li>A plataforma <strong>não fornece</strong> a chave da NF-e (ex: Shopify, Nuvemshop, WooCommerce, VNDA)</li>
            <li>Você cria envios <strong>manualmente</strong> pelo app sem informar nota fiscal</li>
            <li>Você <strong>não emite NF-e</strong> para esse tipo de mercadoria</li>
          </ul>

          <hr />

          <h2>Como imprimir a DACE</h2>
          <ol>
            <li>Acesse a tela de <strong>Impressão de Etiquetas</strong> normalmente.</li>
            <li>As DACEs já aparecem <strong>automaticamente</strong> junto com as etiquetas.</li>
            <li>Para ver <strong>somente as DACEs</strong>, ative o toggle <strong>"Exibir apenas DC-e"</strong>.</li>
            <li>Clique em <strong>"Imprimir Etiquetas"</strong> para imprimir tudo junto.</li>
            <li>Para baixar os PDFs, clique em <strong>"Baixar DC-e (PDF Completo)"</strong>.</li>
          </ol>

          <hr />

          <h2>Perguntas Frequentes</h2>

          <h4>Preciso fazer alguma configuração?</h4>
          <p><strong>Não.</strong> A BRCom já está configurada para solicitar a DC-e automaticamente.</p>

          <h4>Quanto custa a DC-e?</h4>
          <p>A emissão da DC-e pelos Correios é <strong>gratuita</strong>.</p>

          <h4>Preciso imprimir a DACE?</h4>
          <p><strong>Sim.</strong> A DACE deve ser impressa e fixada no pacote.</p>

          <h4>Preciso regerar a etiqueta, como faço?</h4>
          <p>Use o botão <strong>"Regerar Etiqueta"</strong> na tela de rastreamento (menu de ações do objeto). Uma nova pré-postagem será criada automaticamente nos Correios.</p>

          <hr />

          <h2>Resumo</h2>
          <table>
            <thead>
              <tr><th>Situação</th><th>O que muda?</th><th>Ação</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Chave NF-e disponível na criação</td>
                <td>Nada muda</td>
                <td>Nenhuma</td>
              </tr>
              <tr>
                <td>NF-e emitida depois</td>
                <td>DC-e automática</td>
                <td>Imprimir DACE</td>
              </tr>
              <tr>
                <td>Não emite NF-e</td>
                <td>DC-e automática</td>
                <td>Imprimir DACE</td>
              </tr>
            </tbody>
          </table>

          <div style={{ background: "#ddf4ff", borderLeft: "4px solid #54aeff", borderRadius: 4, padding: "14px 16px", margin: "16px 0" }}>
            <strong>Dúvidas?</strong> Entre em contato com o SAC BRCom: <strong>(21) 99837-0244</strong>
          </div>

          {!reachedEnd && (
            <p style={{ textAlign: "center", color: "#999", marginTop: 20, fontSize: 13 }}>
              Role até o final para habilitar o botão de fechar
            </p>
          )}
        </div>
        <div className={styles.footer}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={() => setDontShowAgain(!dontShowAgain)}
            />
            <span>Não exibir novamente</span>
          </label>
          <button
            className={styles.closeButton}
            disabled={!reachedEnd}
            onClick={handleClose}
          >
            {reachedEnd ? "Entendi" : "Role até o final"}
          </button>
        </div>
      </div>
    </div>
  );
};

export { AnnouncementModal };
